import { ENTERPRISE_PLAN } from "@/constants/pricing";

interface EnterpriseContactButtonProps {
  onContact: () => void;
  /** Store already has an open request, so the CTA edits it instead of starting over */
  hasOpenRequest?: boolean;
  label?: string;
  className?: string;
}

export function EnterpriseContactButton({
  onContact,
  hasOpenRequest = false,
  label,
  className = "",
}: EnterpriseContactButtonProps) {
  return (
    <button
      onClick={onContact}
      className={`w-full py-3 px-6 rounded-lg font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors duration-200 ${className}`}
    >
      {label ?? (hasOpenRequest ? "Update your request" : "Get in touch")}
    </button>
  );
}

interface EnterprisePlanCardProps {
  onContact: () => void;
  hasOpenRequest?: boolean;
  /** False when the surrounding page renders plan buttons in a separate row */
  showCta?: boolean;
  ctaLabel?: string;
  className?: string;
}

export function EnterprisePlanCard({
  onContact,
  hasOpenRequest = false,
  showCta = true,
  ctaLabel,
  className = "",
}: EnterprisePlanCardProps) {
  return (
    <div
      className={`relative flex flex-col rounded-xl border-2 border-border bg-card p-6 transition-all duration-200 hover:border-brand-300 hover:shadow-lg ${className}`}
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-foreground text-background text-xs font-semibold rounded-full whitespace-nowrap">
        Custom pricing
      </div>

      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-foreground mb-2">
          {ENTERPRISE_PLAN.name}
        </h3>
        <div className="mb-2">
          <span className="text-3xl font-bold text-foreground">
            {ENTERPRISE_PLAN.price}
          </span>
        </div>
        <p className="text-foreground font-medium">{ENTERPRISE_PLAN.revenue}</p>
        <p className="mt-1 text-sm text-muted-foreground">{ENTERPRISE_PLAN.tagline}</p>
        {hasOpenRequest && (
          <p className="mt-3 inline-block px-3 py-1 bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300 rounded-full text-sm font-semibold">
            Request received — we'll reach out
          </p>
        )}
      </div>

      <ul className="space-y-2 mb-8">
        {ENTERPRISE_PLAN.features.map((feature) => (
          <li key={feature} className="flex items-start text-sm">
            <svg
              className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {showCta && (
        <EnterpriseContactButton
          onContact={onContact}
          hasOpenRequest={hasOpenRequest}
          label={ctaLabel}
          className="mt-auto"
        />
      )}
    </div>
  );
}
